import TokenAvatar from '@components/TokenAvatar'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { Text, cn } from '@ui'
import React, { FC, useMemo, useState } from 'react'

interface PortfolioChartProps {
  balances: TokenBalance[]
  totalUsdBalance: number
}

// Segment fills by rank: the heritage hues, then a neutral for "Others".
const CHART_COLORS = ['bg-cyan', 'bg-yellow', 'bg-magenta', 'bg-fg-3']

interface ChartSegment {
  symbol: string
  logo?: string
  percentage: number
  /** A Tailwind background utility bound to a Plug token. */
  color: string
  isOthers?: boolean
}

export const PortfolioChart: FC<PortfolioChartProps> = ({ balances, totalUsdBalance }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const chartData = useMemo((): ChartSegment[] => {
    if (totalUsdBalance === 0) return []

    const sorted = [...balances]
      .filter((b) => (b.usdBalance || 0) > 0.01)
      .sort((a, b) => (b.usdBalance || 0) - (a.usdBalance || 0))

    const top3 = sorted.slice(0, 3)
    const others = sorted.slice(3)

    const segments: ChartSegment[] = top3.map((balance, index) => ({
      symbol: balance.token.symbol,
      logo: balance.token.logo,
      percentage: ((balance.usdBalance || 0) / totalUsdBalance) * 100,
      color: CHART_COLORS[index],
    }))

    if (others.length > 0) {
      const othersTotal = others.reduce((sum, b) => sum + (b.usdBalance || 0), 0)
      const percentage = (othersTotal / totalUsdBalance) * 100

      if (percentage > 0.1) {
        segments.push({
          symbol: 'Others',
          percentage,
          color: CHART_COLORS[3],
          isOthers: true,
        })
      }
    }

    return segments
  }, [balances, totalUsdBalance])

  if (chartData.length === 0) return null

  return (
    <div className="mt-2 flex w-full flex-col items-center gap-2">
      <div className="flex h-1.5 w-full overflow-hidden rounded-pill">
        {chartData.map((segment, index) => (
          <div
            key={index}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            className={cn(
              'h-full cursor-pointer transition-[opacity,transform] duration-150 ease-out',
              segment.color,
              hoveredIndex === null || hoveredIndex === index ? 'opacity-100' : 'opacity-40',
              hoveredIndex === index ? 'scale-y-150' : 'scale-y-100'
            )}
            style={{ width: `${segment.percentage}%` }}
          />
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
        {chartData.map((segment, index) => (
          <div
            key={index}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            className={cn(
              'flex cursor-pointer items-center gap-1.5 transition-opacity duration-150',
              hoveredIndex === null || hoveredIndex === index ? 'opacity-100' : 'opacity-50'
            )}
          >
            {segment.isOthers ? (
              <span className={cn('size-3.5 shrink-0 rounded-full', segment.color)} />
            ) : (
              <TokenAvatar logo={segment.logo} size={15} />
            )}
            <Text as="span" variant="caption" tone="fg-2">
              {segment.symbol}
            </Text>
            <Text as="span" variant="caption" tone="fg-3" weight="regular" tabular>
              {segment.percentage.toFixed(1)}%
            </Text>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PortfolioChart
